import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

/**
 * Custom RouteReuseStrategy that preserves components when navigating to/from the editor.
 *
 * This prevents the album form from being destroyed when opening the editor,
 * allowing users to return to their unsaved changes.
 */
@Injectable()
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  private storedRoutes = new Map<string, DetachedRouteHandle>();

  /**
   * Route config paths that should be cached when navigating away.
   * These are the `path` values from the route config, not full URLs.
   */
  private readonly routesToCache = new Set([
    '',                              // Products list root (path: '' under products)
    ':categoryId',                   // Products with category (path: ':categoryId' under products)
    ':categoryId/drafts/:draftId'    // Path-based draft URL — caching this preserves the
                                     // products-list component instance across the
                                     // editor trip, so save+exit / return doesn't
                                     // re-mount and re-trigger the "A restaurar
                                     // projeto..." overlay.
  ]);

  /**
   * Parent route paths - if the route's parent has this path, it's cacheable
   */
  private readonly cacheableParentPaths = new Set([
    'products'
  ]);

  /**
   * Get a unique key for the route.
   *
   * Uses ACTUAL url segments (e.g. "1" / "drafts" / "795550") rather than
   * route-config patterns (":categoryId/drafts/:draftId"), so two drafts
   * cache to two distinct entries instead of overwriting each other.
   */
  private getRouteKey(route: ActivatedRouteSnapshot): string {
    // Build the full path from root to this route, walking the snapshot
    // tree and concatenating each level's matched url segments.
    const segments: string[] = [];
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      if (current.url.length > 0) {
        for (let i = current.url.length - 1; i >= 0; i--) {
          segments.unshift(current.url[i].path);
        }
      }
      current = current.parent;
    }

    // Include query params for uniqueness (e.g., wizard step/collectionId).
    const path = segments.join('/');
    const queryParams = route.queryParams;
    const queryString = Object.keys(queryParams)
      // Exclude transient params: `newProject`, and `albumDesign` (the Album Design
      // graduation flag) — otherwise `?albumDesign=1` on entry would key to a
      // different cache slot than the editor's return URL (which drops it),
      // fragmenting the cache and wiping the album form on a cover-editor round-trip.
      .filter(key => key !== 'newProject' && key !== 'albumDesign')
      .sort()
      .map(key => `${key}=${queryParams[key]}`)
      .join('&');

    return queryString ? `${path}?${queryString}` : path;
  }

  /**
   * Check if this route should be cached.
   * A route is cacheable if:
   * 1. Its path matches a cacheable pattern, AND
   * 2. Any ancestor path is in the cacheable parent paths
   */
  private shouldCacheRoute(route: ActivatedRouteSnapshot): boolean {
    const path = route.routeConfig?.path ?? '';

    // Check if route path is cacheable
    const isPathCacheable = this.routesToCache.has(path) ||
      Array.from(this.routesToCache).some(pattern => this.matchesPattern(path, pattern));

    if (!isPathCacheable) {
      return false;
    }

    // Check if any ancestor path indicates this is under a cacheable parent
    // This handles lazy-loaded routes where there may be intermediate route snapshots
    let ancestor = route.parent;
    const ancestorPaths: string[] = [];

    while (ancestor) {
      const ancestorPath = ancestor.routeConfig?.path ?? '';
      if (ancestorPath) {
        ancestorPaths.push(ancestorPath);
      }
      if (this.cacheableParentPaths.has(ancestorPath)) {
        console.log('[RouteReuse] Route is cacheable:', { path, ancestorPaths });
        return true;
      }
      ancestor = ancestor.parent;
    }

    // Debug: Log when route is not cached
    console.log('[RouteReuse] Route NOT cacheable:', { path, ancestorPaths });
    return false;
  }

  /**
   * Simple pattern matching (supports :param placeholders)
   */
  private matchesPattern(path: string, pattern: string): boolean {
    if (path === pattern) return true;

    // Handle :param patterns - the pattern might have :param which matches any value
    if (pattern.startsWith(':') && path && !path.includes('/')) {
      return true;
    }

    return false;
  }

  /**
   * Determines if a route should be reused.
   * Called when navigating between routes.
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    // Default behavior: reuse if same route config
    return future.routeConfig === curr.routeConfig;
  }

  /**
   * Determines if this route should be detached (cached) when navigating away.
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const shouldCache = this.shouldCacheRoute(route);
    if (shouldCache) {
      console.log('[RouteReuse] shouldDetach=true for route:', route.routeConfig?.path);
    }
    return shouldCache;
  }

  /**
   * One-shot override for the NEXT store() key. Used when a URL is being
   * promoted in place (e.g. products form → its freshly-saved draft path):
   * the live component detaches under the OLD route's key, but the
   * navigation lands on the NEW url — storing under the destination key
   * lets shouldAttach() re-attach the SAME instance instead of cold-mounting
   * and re-restoring state that is already live.
   */
  private storeKeyOverride: string | null = null;

  storeNextAs(key: string): void {
    this.storeKeyOverride = key;
  }

  /**
   * Store the detached route handle.
   */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (handle) {
      const key = this.storeKeyOverride ?? this.getRouteKey(route);
      if (this.storeKeyOverride) {
        console.log('[RouteReuse] Storing route under override key:', key);
      } else {
        console.log('[RouteReuse] Storing route:', key);
      }
      this.storedRoutes.set(key, handle);
    }
    this.storeKeyOverride = null;
  }

  /**
   * Determines if a stored route should be reattached.
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getRouteKey(route);
    const hasStored = this.storedRoutes.has(key);

    console.log('[RouteReuse] shouldAttach check:', {
      key,
      hasStored,
      storedKeys: Array.from(this.storedRoutes.keys())
    });

    if (hasStored) {
      console.log('[RouteReuse] Will reattach route:', key);
    }

    return hasStored;
  }

  /**
   * Retrieve the stored route handle.
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getRouteKey(route);
    const handle = this.storedRoutes.get(key) || null;

    if (handle) {
      console.log('[RouteReuse] Retrieving route:', key);
      // Remove after retrieval - we only want to restore once
      // Comment out the next line if you want persistent caching
      // this.storedRoutes.delete(key);
    }

    return handle;
  }

  /**
   * Clear all stored routes.
   * Call this when the user explicitly leaves the flow (e.g., navigates to home).
   */
  clearStoredRoutes(): void {
    console.log('[RouteReuse] Clearing all stored routes');
    this.storedRoutes.clear();
  }

  /**
   * Clear a specific stored route.
   */
  clearRoute(routeKey: string): void {
    if (this.storedRoutes.has(routeKey)) {
      console.log('[RouteReuse] Clearing route:', routeKey);
      this.storedRoutes.delete(routeKey);
    }
  }

  /**
   * Evict every cached handle for a given draft id (any `.../drafts/{id}` key,
   * regardless of query params). Called when the user explicitly RE-ENTERS a
   * draft flow — e.g. "Iniciar encomenda" on the Album Design board — so the
   * products-list re-mounts fresh instead of resurrecting the previous, unsaved
   * collection/cover selection.
   */
  clearRoutesForDraft(draftId: number): void {
    const needle = `/drafts/${draftId}`;
    for (const key of Array.from(this.storedRoutes.keys())) {
      if (key.includes(needle)) {
        console.log('[RouteReuse] Clearing draft route:', key);
        this.storedRoutes.delete(key);
      }
    }
  }
}
